/* =====================================================================
   Bluewud — search intent parser.

   Shopify's storefront search has no query understanding: it splits the
   query into words and loosely OR/fuzzy-matches them. So "coffee table
   under 3000" searches for the literal words "under" and "3000" and
   happily returns ₹6,449 tables. Meanwhile /search DOES honour
   filter.v.price.gte / .lte and sort_by — nothing was connecting the
   shopper's words to those parameters.

   This file is that connection. It:
     • parses budget intent  ("under 3000", "2k to 5k", "around 4000",
       "below rs.3,000/-", "1 lakh") into filter.v.price.* params;
     • parses sort intent    ("cheapest", "best selling") into sort_by;
     • rewrites terms the catalogue does not use ("almirah" → "wardrobe",
       "cofee table" → "coffee table") into terms it does;
     • shows what it did as removable chips, so the shopper can always
       see and undo it. Never filter silently.

   Design rules followed here:
     - REPLACE unknown words, never ADD synonyms. Shopify OR-matches, so
       adding words broadens the result set — the opposite of the goal.
     - Only price and sort become hard filters. Colour/material/room stay
       as keywords: the catalogue has no colour metafield, and the Color
       option values are compound ("Maple & White"), so a colour filter
       would silently drop matching products.
     - Everything is wrapped so a parser bug can never break the page.
   ===================================================================== */
(function () {
  'use strict';

  var MARKER = 'bwq';          // "this URL has already been parsed"
  var RAW    = 'bwraw';        // the shopper's original words, for the chip

  /* ---------------------------------------------------------------
     1. Vocabulary the catalogue actually uses.
     Derived from all 407 live products (title + product_type, words
     appearing in 2+ products). We only rewrite a word if it is NOT in
     here — that keeps us from "correcting" real product names.
     --------------------------------------------------------------- */
  var CATALOGUE_WORDS = ('bed bedroom bookshelf book books cabinet center centre chair chairs coffee corner '
    + 'cupboard desk dining display door doors dresser dressing drawer drawers engineered entertainment '
    + 'holder key keychain kitchen laptop mirror mirrors monitor mount mounted office organiser rack racks '
    + 'seater shelf shelves shoe side sofa spice stand standing stool storage study table tables tray '
    + 'unit wall wardrobe wood wooden').split(' ');

  var IN_CATALOGUE = Object.create(null);
  CATALOGUE_WORDS.forEach(function (w) { IN_CATALOGUE[w] = true; });

  /* ---------------------------------------------------------------
     2. Term rewrites — Indian-English furniture vocabulary and typos.
     Key = what shoppers type and the catalogue does NOT contain.
     Value = the words the catalogue does contain.
     Longest keys are matched first (see rewriteTerms).
     --------------------------------------------------------------- */
  var REWRITES = {
    /* wardrobe family */
    'almirah': 'wardrobe', 'almira': 'wardrobe', 'almirha': 'wardrobe', 'alimrah': 'wardrobe',
    'almyra': 'wardrobe', 'aalmirah': 'wardrobe', 'wardrob': 'wardrobe', 'wardobe': 'wardrobe',
    'wadrobe': 'wardrobe', 'wardrobes': 'wardrobe', 'closet': 'wardrobe', 'cloth cabinet': 'wardrobe',
    'clothes cabinet': 'wardrobe', 'cupboard': 'wardrobe', 'cupboards': 'wardrobe',
    'cubboard': 'wardrobe', 'cubard': 'wardrobe',

    /* coffee / centre table family */
    'teapoy': 'coffee table', 'tea poy': 'coffee table', 'tea table': 'coffee table',
    'cofee table': 'coffee table', 'coffe table': 'coffee table', 'coffey table': 'coffee table',
    'cofee': 'coffee', 'coffe': 'coffee', 'cofffee': 'coffee',
    'centr table': 'centre table', 'senter table': 'centre table',

    /* tv unit family */
    'tv stand': 'tv unit', 'tv cabinet': 'tv unit', 'tv table': 'tv unit', 'tv rack': 'tv unit',
    'television unit': 'tv unit', 'television stand': 'tv unit', 'television cabinet': 'tv unit',
    'tv console': 'tv unit', 'media unit': 'tv unit', 'media console': 'tv unit',
    'tv unit cabinet': 'tv unit', 'tvunit': 'tv unit', 'tv-unit': 'tv unit',

    /* shoe storage */
    'shoerack': 'shoe rack', 'shoe-rack': 'shoe rack', 'shoes rack': 'shoe rack',
    'shoe cabinet': 'shoe rack', 'shoe stand': 'shoe rack', 'shoe shelf': 'shoe rack',
    'shoe organiser': 'shoe rack', 'shoe organizer': 'shoe rack', 'shue rack': 'shoe rack',

    /* study / work */
    'computer desk': 'computer table', 'computor table': 'computer table', 'writing desk': 'study table',
    'writing table': 'study table', 'office table': 'study table', 'work table': 'study table',
    'work desk': 'study table', 'workstation': 'study table', 'study desk': 'study table',
    'studytable': 'study table', 'reading table': 'study table',

    /* bookshelf */
    'book-shelf': 'book shelf', 'book rack': 'bookshelf',
    'bookcase': 'bookshelf', 'book case': 'bookshelf', 'book self': 'bookshelf',
    'bookshelves': 'bookshelf', 'book shelves': 'bookshelf', 'bookrack': 'bookshelf',

    /* dressing */
    'vanity': 'dressing table', 'vanity table': 'dressing table', 'makeup table': 'dressing table',
    'dressing mirror': 'dressing table', 'dresing table': 'dressing table',

    /* sofa / seating */
    'sofa set': 'sofa', 'sofaset': 'sofa', 'couch': 'sofa', 'settee': 'sofa',
    'sofa cum bed': 'sofa', 'sofa bed': 'sofa', 'sofacumbed': 'sofa', 'diwan': 'sofa',
    'divan': 'sofa', 'lounger': 'sofa',

    /* side tables */
    'end table': 'side table', 'bedside table': 'side table', 'nightstand': 'side table',
    'night stand': 'side table', 'corner table': 'side table',
    'lamp table': 'side table',

    /* storage misc */
    'chest of drawers': 'drawer', 'chester drawer': 'drawer', 'crockery unit': 'storage cabinet',
    'crockery cabinet': 'storage cabinet', 'sideboard': 'storage cabinet',
    'shoe box': 'shoe rack', 'storage box': 'storage',
    'wall rack': 'wall shelf', 'floating shelf': 'wall shelf',
    'wall mounted shelf': 'wall shelf', 'wall bracket': 'wall shelf',

    /* dining */
    'dinning table': 'dining table', 'dining set': 'dining table', 'dinning set': 'dining table',
    'dineing table': 'dining table', 'khana table': 'dining table',

    /* materials / misc typos.
       Deliberately NOT mapped: sheesham, plywood, solid wood. Bluewud sells
       engineered wood — rewriting those to "wood" would return products the
       shopper did not ask for. An honest zero beats a misleading match. */
    'particle board': 'engineered wood', 'mdf': 'engineered wood',
    'wodden': 'wooden', 'furnitur': 'furniture', 'furnature': 'furniture', 'furniture': '',
    'matress': 'mattress', 'mirrror': 'mirror', 'shelfs': 'shelves',
    'keyholder': 'key holder', 'key stand': 'key holder', 'key hanger': 'key holder'
  };

  /* Terms that DO appear in the catalogue but are heavily out-voted by a
     better canonical word, so rewriting still helps. "cupboard" is in 5
     titles; "wardrobe" is in 28 — a shopper typing cupboard wants all 28. */
  var ALWAYS_REWRITE = { cupboard: true };

  /* Multi-word keys must be tried before single-word ones. */
  var REWRITE_KEYS = Object.keys(REWRITES).sort(function (a, b) { return b.length - a.length; });

  /* ---------------------------------------------------------------
     3. Money parsing — Indian conventions included.
     --------------------------------------------------------------- */
  var MULT = {
    k: 1e3, thousand: 1e3, thousands: 1e3, hazaar: 1e3, hazar: 1e3, hzr: 1e3,
    lakh: 1e5, lakhs: 1e5, lac: 1e5, lacs: 1e5, lakhh: 1e5,
    cr: 1e7, crore: 1e7, crores: 1e7
  };

  /* rs. / ₹ / inr prefix, digits with , or . grouping, optional k/lakh suffix, optional /- or rs suffix */
  var NUM = '(?:rs\\.?|inr|₹)?\\s*(\\d[\\d,]*(?:\\.\\d+)?)\\s*(k|thousand|thousands|hazaar|hazar|hzr|lakhs?|lacs?|crores?|cr)?\\s*(?:rupees|rs\\.?|inr|\\/-|-)?';

  function toAmount(digits, suffix) {
    var n = parseFloat(String(digits).replace(/,/g, ''));
    if (!isFinite(n)) return null;
    if (suffix) {
      var m = MULT[suffix.toLowerCase()];
      if (m) n = n * m;
    }
    // A bare "3" almost never means ₹3 — but "3k"/"3 lakh" already scaled above.
    if (!suffix && n > 0 && n < 100) return null;
    return Math.round(n);
  }

  var MAX_WORDS = 'under|below|less\\s+than|lesser\\s+than|lower\\s+than|upto|up\\s*to|within|'
    + 'max(?:imum)?|not\\s+more\\s+than|no\\s+more\\s+than|cheaper\\s+than|beneath|<=?';
  var MIN_WORDS = 'above|over|more\\s+than|greater\\s+than|higher\\s+than|starting\\s+(?:at|from)|'
    + 'min(?:imum)?|at\\s+least|>=?';
  var NEAR_WORDS = 'around|approx(?:imately)?|about|near(?:\\s+about)?|roughly|near';

  /* Ordered: ranges first, then near, then max, then min. */
  var PRICE_RULES = [
    { re: new RegExp('(?:between|from|for)?\\s*' + NUM + '\\s*(?:-|–|—|to|and)\\s*' + NUM, 'i'),
      apply: function (m, out) {
        var a = toAmount(m[1], m[2]), b = toAmount(m[3], m[4]);
        if (a == null || b == null) return false;
        out.priceMin = Math.min(a, b); out.priceMax = Math.max(a, b); return true;
      } },
    { re: new RegExp('(?:' + NEAR_WORDS + ')\\s*' + NUM, 'i'),
      apply: function (m, out) {
        var n = toAmount(m[1], m[2]); if (n == null) return false;
        out.priceMin = Math.round(n * 0.8); out.priceMax = Math.round(n * 1.2); return true;
      } },
    { re: new RegExp('(?:' + MAX_WORDS + ')\\s*' + NUM, 'i'),
      apply: function (m, out) {
        var n = toAmount(m[1], m[2]); if (n == null) return false;
        out.priceMax = n; return true;
      } },
    { re: new RegExp('(?:' + MIN_WORDS + ')\\s*' + NUM, 'i'),
      apply: function (m, out) {
        var n = toAmount(m[1], m[2]); if (n == null) return false;
        out.priceMin = n; return true;
      } }
  ];

  /* ---------------------------------------------------------------
     4. Sort intent. Only values /search actually accepts.
     --------------------------------------------------------------- */
  var SORT_RULES = [
    { re: /\b(cheapest|lowest\s+price|price\s+low\s+to\s+high|low\s+to\s+high|least\s+expensive)\b/i, sort: 'price-ascending',  label: 'Lowest price first' },
    { re: /\b(most\s+expensive|highest\s+price|price\s+high\s+to\s+low|high\s+to\s+low)\b/i,          sort: 'price-descending', label: 'Highest price first' },
    { re: /\b(best\s*sell(?:ing|er)s?|bestsellers?|top\s+sell(?:ing|ers?)|most\s+popular|popular)\b/i, sort: 'best-selling',     label: 'Best selling' },
    /* soft budget words: nudge the ordering, do not invent a hard cap */
    { re: /\b(cheap|budget|affordable|economical|low\s+budget)\b/i,                                    sort: 'price-ascending',  label: 'Lowest price first' }
  ];

  /* ---------------------------------------------------------------
     5. The parser.
     --------------------------------------------------------------- */
  function rewriteTerms(text) {
    var changed = [];
    var out = ' ' + text + ' ';
    REWRITE_KEYS.forEach(function (key) {
      // Only rewrite if the shopper's word is not itself catalogue vocabulary
      // (otherwise we would "correct" real product names).
      if (key.indexOf(' ') === -1 && IN_CATALOGUE[key] && !ALWAYS_REWRITE[key]) return;
      var re = new RegExp('(^|\\s)' + key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?=\\s|$)', 'gi');
      if (!re.test(out)) return;
      re.lastIndex = 0;
      out = out.replace(re, '$1' + REWRITES[key]);
      changed.push(key);
    });
    return { text: out.replace(/\s+/g, ' ').trim(), changed: changed };
  }

  function parse(raw) {
    var out = { q: raw, priceMin: null, priceMax: null, sort: null, sortLabel: null, rewrote: [], changed: false };
    var text = ' ' + String(raw).toLowerCase().replace(/\s+/g, ' ').trim() + ' ';

    for (var i = 0; i < PRICE_RULES.length; i++) {
      var m = text.match(PRICE_RULES[i].re);
      if (m && PRICE_RULES[i].apply(m, out)) { text = text.replace(m[0], ' '); break; }
    }

    for (var j = 0; j < SORT_RULES.length; j++) {
      var s = text.match(SORT_RULES[j].re);
      if (s) {
        out.sort = SORT_RULES[j].sort;
        out.sortLabel = SORT_RULES[j].label;
        text = text.replace(s[0], ' ');
        break;
      }
    }

    /* Leftover currency noise once the amount is gone. */
    text = text.replace(/\b(rs\.?|inr|rupees|₹|price|budget|range|only|please|for)\b/gi, ' ');

    var rw = rewriteTerms(text.replace(/\s+/g, ' ').trim());
    out.q = rw.text;
    out.rewrote = rw.changed;
    out.changed = !!(out.priceMin || out.priceMax || out.sort || rw.changed.length);
    return out;
  }

  /* ---------------------------------------------------------------
     5b. Category routing.

     Shopify's /search ORs the terms and matches title, body and tags, so
     "coffee tables under 3000" returned 103 results padded out with
     bookshelves and study tables. When the shopper has clearly named one of
     our categories we send them to that category's collection instead,
     which contains only that category.

     Ordered longest phrase first so "bed side table" beats "side table",
     and "wall mounted tv unit" beats "tv unit". Handles were verified
     against /collections.json on 2026-08-21 -- if a collection is renamed
     or deleted this map MUST be updated or the redirect will 404.
     --------------------------------------------------------------- */
  var CATEGORY_MAP = [
    ['wall mounted tv unit', 'wall-mounted-tv-stand-tv-unit'],
    ['wall mount tv unit', 'wall-mounted-tv-stand-tv-unit'],
    ['wall mounted bookshelf', 'wall-mounted-bookshelves'],
    ['wall mounted book shelf', 'wall-mounted-bookshelves'],
    ['set top box stand', 'set-top-box-stand'],
    ['dressing table', 'dressing-vanity-mirrors'],
    ['vanity mirror', 'dressing-vanity-mirrors'],
    ['bed side table', 'bed-side-tables'],
    ['bedside table', 'bed-side-tables'],
    ['night stand', 'bed-side-tables'],
    ['side table', 'bed-side-tables'],
    ['office organizer', 'office-organizers'],
    ['file organizer', 'file-organizers'],
    ['microwave stand', 'microwave-stand'],
    ['modular kitchen', 'modular-kitchen'],
    ['floating shelf', 'floating-shelves'],
    ['floating shelve', 'floating-shelves'],
    ['display shelf', 'display-shelves'],
    ['display unit', 'display-shelves'],
    ['corner shelf', 'corner-shelves'],
    ['corner shelve', 'corner-shelves'],
    ['crockery unit', 'crockery-unit'],
    ['kitchen rack', 'kitchen-racks'],
    ['kitchen cabinet', 'floor-standing-kitchen-cabinet'],
    ['remote holder', 'remote-holders'],
    ['coffee table', 'coffee-table'],
    ['centre table', 'coffee-table'],
    ['center table', 'coffee-table'],
    ['dining table', 'dining-tables'],
    ['study table', 'study-table'],
    ['computer table', 'study-table'],
    ['laptop table', 'portable-laptop-study-tables'],
    ['study desk', 'study-table'],
    ['wall shelf', 'wall-shelves'],
    ['wall shelve', 'wall-shelves'],
    ['book shelf', 'book-shelves'],
    ['book shelve', 'book-shelves'],
    ['bookshelf', 'book-shelves'],
    ['bookshelve', 'book-shelves'],
    ['bookcase', 'book-shelves'],
    ['shoe rack', 'wooden-shoe-rack-shoe-stand-with-door'],
    ['shoe stand', 'wooden-shoe-rack-shoe-stand-with-door'],
    ['shoe cabinet', 'wooden-shoe-rack-shoe-stand-with-door'],
    ['spice rack', 'spice-rack'],
    ['key holder', 'key-holders'],
    ['king size bed', 'king-size-bed'],
    ['queen size bed', 'queen-size-bed'],
    ['double bed', 'double-bed'],
    ['tv unit', 'tv-unit-furniture'],
    ['tv stand', 'tv-unit-furniture'],
    ['tv cabinet', 'tv-unit-furniture'],
    ['ottoman', 'ottoman-pouffe'],
    ['pouffe', 'ottoman-pouffe'],
    ['wardrobe', 'wardrobe'],
    ['almirah', 'wardrobe'],
    ['cupboard', 'wardrobe'],
    ['mandir', 'mandirs'],
    ['temple', 'mandirs'],
    ['dresser', 'dressing-vanity-mirrors'],
    ['mirror', 'mirrors'],
    ['living room', 'living-room'],
    ['bedroom', 'bed-room'],
    ['bed room', 'bed-room'],
    ['sofa', 'sofa'],
    ['desk', 'study-table']
  ];

  /* Words carrying no product meaning: a query that is a category plus only
     these still counts as a clean category request. */
  var FILLER = /^(?:s|es|the|a|an|and|or|of|for|with|in|my|our|new|best|top|good|nice|cheap|online|buy|shop|show|me|india|indian|wooden|wood|engineered)$/;

  function matchCategory(q) {
    if (!q) return null;
    var text = ' ' + q.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim() + ' ';

    for (var i = 0; i < CATEGORY_MAP.length; i++) {
      var phrase = CATEGORY_MAP[i][0];
      var idx = text.indexOf(' ' + phrase);
      if (idx === -1) continue;

      /* Only redirect when the category IS the request. If the shopper added
         real extra words ("coffee table with storage brown") a collection
         would silently drop them, so those stay on /search. */
      var rest = (text.slice(0, idx) + ' ' + text.slice(idx + phrase.length + 1))
        .replace(/\s+/g, ' ').trim();
      var leftover = rest ? rest.split(' ').filter(function (w) { return w && !FILLER.test(w); }) : [];
      if (leftover.length) continue;

      return { handle: CATEGORY_MAP[i][1], label: phrase };
    }
    return null;
  }

  /* ---------------------------------------------------------------
     6. URL building.
     --------------------------------------------------------------- */
  /* Shopify's /search exposes only these; anything else is ignored there. */
  var SEARCH_SORTS = ['relevance', 'price-ascending', 'price-descending'];

  function inr(n) {
    try { return '₹' + Number(n).toLocaleString('en-IN'); }
    catch (e) { return '₹' + n; }
  }

  function buildUrl(parsed, rawQuery, basePath) {
    // An empty query after stripping ("under 3000") is a browse, not a search.
    var browseAll = !parsed.q;

    /* A named category beats keyword search: send them to the collection,
       which holds only that category. Price filters and sort still apply,
       and collections accept the full sort list, not just the three that
       /search allows. */
    var cat = browseAll ? null : matchCategory(parsed.q);
    if (cat) {
      var curl = new URL('/collections/' + cat.handle, window.location.origin);
      if (parsed.priceMin) curl.searchParams.set('filter.v.price.gte', parsed.priceMin);
      if (parsed.priceMax) curl.searchParams.set('filter.v.price.lte', parsed.priceMax);
      if (parsed.sort) curl.searchParams.set('sort_by', parsed.sort);
      curl.searchParams.set(MARKER, '1');
      curl.searchParams.set(RAW, rawQuery || parsed.q);
      return curl;
    }

    var url = new URL(browseAll ? '/collections/all' : (basePath || '/search'), window.location.origin);
    if (!browseAll) {
      url.searchParams.set('q', parsed.q);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
    }
    if (parsed.priceMin) url.searchParams.set('filter.v.price.gte', parsed.priceMin);
    if (parsed.priceMax) url.searchParams.set('filter.v.price.lte', parsed.priceMax);
    // /search accepts only these three (confirmed against search.sort_options);
    // best-selling is collection-only, so dropping it beats emitting a param
    // Shopify silently ignores while we show a chip claiming it was applied.
    if (parsed.sort && (browseAll || SEARCH_SORTS.indexOf(parsed.sort) !== -1)) {
      url.searchParams.set('sort_by', parsed.sort);
    }
    url.searchParams.set(MARKER, '1');
    if (rawQuery && rawQuery !== parsed.q) url.searchParams.set(RAW, rawQuery);
    return url;
  }

  /* ---------------------------------------------------------------
     7. Intercept search submits, anywhere on the site.
     Runs in the capture phase so it beats empire.js's LiveSearch handler
     (which deep-clones the form and submits the clone). bluewud-sku-search.js
     also listens in capture, but only acts on whitespace-free SKU-shaped
     queries and stops propagation for those — so the two never overlap.
     --------------------------------------------------------------- */
  function onSubmit(e) {
    try {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = (form.getAttribute('action') || '');
      if (action.indexOf('/search') === -1) return;

      var input = form.querySelector('input[name="q"]');
      if (!input) return;
      var raw = (input.value || '').trim();
      if (!raw || raw.length > 150) return;

      /* SKU queries belong to bluewud-sku-search.js. Both files listen on
         document in the capture phase, so without this the winner is decided
         by script load order -- and when this file won, a SKU was rewritten
         into a keyword search that Shopify cannot match, producing the
         "nothing found" the team kept hitting. If the SKU module is absent
         this guard is inert and behaviour is unchanged. */
      if (window.__bwIsSkuQuery && window.__bwIsSkuQuery(raw)) return;

      var parsed = parse(raw);
      if (!parsed.changed) return;              // nothing to add — let it through

      e.preventDefault();
      e.stopPropagation();
      window.location.assign(buildUrl(parsed, raw).toString());
    } catch (err) { /* never block the shopper's search */ }
  }
  document.addEventListener('submit', onSubmit, true);

  /* ---------------------------------------------------------------
     8. On the results page: rewrite a raw URL, then show the chips.
     Handles people who land on /search?q=... from Google, a bookmark,
     or the predictive dropdown's "view all results".
     --------------------------------------------------------------- */
  function currentParams() { return new URL(window.location.href); }

  function maybeRewriteLanding() {
    var url = currentParams();
    if (url.searchParams.get(MARKER)) return false;          // already parsed
    if (url.pathname.indexOf('/search') !== 0) return false;
    var raw = (url.searchParams.get('q') || '').trim();
    if (!raw) return false;
    // Respect a price filter the shopper set by hand.
    if (url.searchParams.get('filter.v.price.gte') || url.searchParams.get('filter.v.price.lte')) return false;

    var parsed = parse(raw);
    // A sort we would drop for /search is not a reason to reload the page.
    var usableSort = parsed.sort && (!parsed.q || SEARCH_SORTS.indexOf(parsed.sort) !== -1);
    if (!parsed.priceMin && !parsed.priceMax && !usableSort && !parsed.rewrote.length) return false;

    var next = buildUrl(parsed, raw);
    // Carry across any other filters already on the URL.
    url.searchParams.forEach(function (v, k) {
      if (k === 'q' || k === MARKER || k === RAW || k === 'type' || k === 'options[prefix]') return;
      if (!next.searchParams.get(k)) next.searchParams.set(k, v);
    });
    window.location.replace(next.toString());
    return true;
  }

  function chip(label, onRemove) {
    var el = document.createElement('span');
    el.className = 'bw-intent-chip';
    var txt = document.createElement('span');
    txt.className = 'bw-intent-chip__label';
    txt.textContent = label;
    el.appendChild(txt);
    if (onRemove) {
      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'bw-intent-chip__x';
      x.setAttribute('aria-label', 'Remove filter: ' + label);
      x.innerHTML = '&#10005;';
      x.addEventListener('click', onRemove);
      el.appendChild(x);
    }
    return el;
  }

  function renderChips() {
    var url = currentParams();
    if (!url.searchParams.get(MARKER)) return;

    var gte = url.searchParams.get('filter.v.price.gte');
    var lte = url.searchParams.get('filter.v.price.lte');
    var sort = url.searchParams.get('sort_by');
    var raw = url.searchParams.get(RAW);
    var q = url.searchParams.get('q') || '';
    if (!gte && !lte && !sort && !raw) return;

    /* IMPORTANT: .productgrid--wrapper is a CSS grid with named template
       areas — an extra in-flow child gets auto-placed into a named cell and
       wrecks the sidebar + card grid. The masthead is display:block and
       already spans both columns, so we nest INSIDE it. */
    var host = document.querySelector('.productgrid--masthead');
    if (!host || host.querySelector('.bw-intent-bar')) return;

    var bar = document.createElement('div');
    bar.className = 'bw-intent-bar';

    if (raw && raw.toLowerCase() !== q.toLowerCase()) {
      var note = document.createElement('span');
      note.className = 'bw-intent-bar__note';
      note.textContent = 'You searched “' + raw + '” — showing ';
      var strong = document.createElement('strong');
      strong.textContent = q || 'everything';
      note.appendChild(strong);
      bar.appendChild(note);
    }

    function without(keys) {
      var u = currentParams();
      keys.forEach(function (k) { u.searchParams.delete(k); });
      u.searchParams.delete('page');
      // Re-searching the cleaned q must not be re-parsed into the same filter.
      u.searchParams.set(MARKER, '1');
      window.location.assign(u.toString());
    }

    var priceLabel = null;
    if (gte && lte) priceLabel = inr(gte) + ' – ' + inr(lte);
    else if (lte)   priceLabel = 'Under ' + inr(lte);
    else if (gte)   priceLabel = 'Above ' + inr(gte);
    if (priceLabel) {
      bar.appendChild(chip(priceLabel, function () {
        without(['filter.v.price.gte', 'filter.v.price.lte']);
      }));
    }

    if (sort) {
      var sl = { 'price-ascending': 'Lowest price first', 'price-descending': 'Highest price first', 'best-selling': 'Best selling' }[sort];
      if (sl) bar.appendChild(chip(sl, function () { without(['sort_by']); }));
    }

    host.appendChild(bar);
  }

  /* The /search sort <select> is plain markup (Liquid has no search.sort_options
     we can rely on here), so reflect the current sort_by from the URL. The
     change handler itself lives in bluewud-collection-enhance.js. */
  function syncSortControl() {
    var sel = document.getElementById('bw_search_sort');
    if (!sel) return;
    var want = currentParams().searchParams.get('sort_by') || 'relevance';
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === want) { sel.selectedIndex = i; return; }
    }
  }

  function start() {
    try {
      if (maybeRewriteLanding()) return;   // navigating away; don't paint
      renderChips();
      syncSortControl();
    } catch (e) { /* chips are cosmetic — never break the page */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* Exposed for testing from the console. */
  window.bwParseSearch = parse;
})();
